import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuxiliaresModule } from '../auxiliares.module';
import { DialogService } from '../dialog/dialog.service';
import { HelperService } from '../servicios/helper.service';
import { LoadingService } from '../servicios/loading.service';

//Información de cada archivo subido
export interface ArchivoSubido {
  url: string;
  descripcion: string;
  fecha: Date;
  nombreOriginal: string;
  size: number;
}

@Component({
  selector: 'app-subir-archivos',
  templateUrl: './subir-archivos.component.html',
  styleUrls: ['./subir-archivos.component.scss'],
  standalone: true,
  imports: [CommonModule, AuxiliaresModule],
})
export class SubirArchivoComponent {
  private helper = inject(HelperService);
  private dialogService = inject(DialogService);
  loading = inject(LoadingService);

  @Input() disabled: boolean = false; //Deshabilitar el botón de subir archivos
  @Input() textoBoton: string = 'Subir archivo'; //Texto que va a tener el botón de subir archivos
  @Input() textoDrag: string = 'Arrastrá y soltá archivos acá o ...'; //Texto que se pone en el recuadro para hacer drag an drop de archivos
  @Input() allowMultiple: boolean = false; //Si se puede subir más de un archivo a la vez
  @Input() maxFiles?: number; // puede ser undefined => sin límite
  @Input() maxSizeMB?: number; // tamaño máximo por archivo en MB, undefined sin límite
  @Input() maxTotalSizeMB?: number; // tamaño máximo total combinado de todos los archivos (MB), undefined sin límite
  @Input() acceptedTypes?: string; // MIME types aceptados separados por coma (image/png,application/pdf)
  @Input() descripcion?: boolean; // para agregar o no descripción al archivo subido, por defecto no se le puede agregar descripción

  @Input() archivos: ArchivoSubido[] = []; //Acá se van guardando los archivos que se suben
  @Output() archivosChange = new EventEmitter<ArchivoSubido[]>();
  @Output() loadingFile = new EventEmitter<boolean>(); //Indica el estado de carga de los archivos cuando se suben

  public isUploading = false;
  public isDragOver = false;

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (this.disabled) return;
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  // Método para calcular tamaño total en bytes de archivos subidos
  get totalSizeBytes(): number {
    return this.archivos.reduce((acc, a) => {
      // Nota: para que esto funcione bien, deberías tener el tamaño del archivo original.
      // Si no tienes, podés almacenar tamaño al subir o estimar a partir de la url (pero no recomendable).
      // Aquí asumiremos que la propiedad tamaño está disponible (deberías agregarla al subir archivo).
      // Si no tienes tamaño, puedes ignorar el totalSizeBytes o pasarlo por otro lado.

      // Por ejemplo, asumamos que archivo tiene size en bytes:
      return acc + (a['size'] || 0);
    }, 0);
  }

  // Convertir bytes a MB con 2 decimales
  bytesToMB(bytes: number): number {
    return Math.round((bytes / (1024 * 1024)) * 100) / 100;
  }

  async onDrop(event: DragEvent) {
    event.preventDefault();
    if (this.disabled) return;
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    if (!this.allowMultiple && fileArray.length > 1) {
      this.helper.notifWarn('Sólo podés subir un archivo.');
      return;
    }

    const fakeEvent = { target: { files } } as unknown as Event;
    this.allowMultiple
      ? await this.setFiles(fakeEvent)
      : await this.setFile(fakeEvent);
  }

  esImagen(nombreArchivo: string): boolean {
    const ext = nombreArchivo.split('.').pop()?.toLowerCase() || '';
    return ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
  }

  getVistaPreviaTipo(archivo: ArchivoSubido): 'imagen' | 'otro' {
    const nombre = archivo.nombreOriginal.toLowerCase();
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(nombre)
      ? 'imagen'
      : 'otro';
  }

  getIconoArchivo(nombre: string): string {
    const ext = nombre.split('.').pop()?.toLowerCase() || '';
    const iconMap: { [key: string]: string } = {
      pdf: 'picture_as_pdf',
      doc: 'description',
      docx: 'description',
      xls: 'grid_on',
      xlsx: 'grid_on',
      ppt: 'slideshow',
      zip: 'folder_zip',
      rar: 'folder_zip',
      txt: 'notes',
    };
    return iconMap[ext] || 'insert_drive_file';
  }

  onFileChange(event: Event): void {
    this.allowMultiple ? this.setFiles(event) : this.setFile(event);
  }

  private esTipoValido(file: File): boolean {
    if (!this.acceptedTypes) return true;
    const tipos = this.acceptedTypes.split(',').map((t) => t.trim());
    return tipos.includes(file.type);
  }

  async setFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (this.maxSizeMB && this.maxSizeMB > 0) {
      const maxBytes = this.maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        this.helper.notifWarn(
          `El archivo "${file.name}" supera el tamaño máximo permitido de ${this.maxSizeMB} MB.`,
        );
        return;
      }
    }

    if (this.maxTotalSizeMB && this.maxTotalSizeMB > 0) {
      const maxTotalBytes = this.maxTotalSizeMB * 1024 * 1024;
      const nuevoTotal = this.totalSizeBytes + file.size;
      if (nuevoTotal > maxTotalBytes) {
        this.helper.notifWarn(
          `No se puede subir "${file.name}" porque superaría el tamaño total máximo permitido de ${this.maxTotalSizeMB} MB.`,
        );
        return;
      }
    }

    if (!this.esTipoValido(file)) {
      this.helper.notifWarn(`Tipo de archivo no permitido: ${file.type}`);
      return;
    }

    if (
      this.maxFiles &&
      this.maxFiles > 0 &&
      this.archivos.length >= this.maxFiles
    ) {
      this.helper.notifWarn(
        `Has alcanzado el máximo de ${this.maxFiles} archivo(s) permitidos.`,
      );
      return;
    }

    const confirmed = await this.dialogService.confirm(
      'Subir archivo',
      `¿Querés subir "${file.name}"?`,
    );
    if (!confirmed) return;

    const nombreArchivo = `${file.name.split('.')[0]}_${Date.now()}.${file.name.split('.').pop()}`;

    this.loadingFile.emit(true);
    this.isUploading = true;

    try {
      const res = await this.helper.subirArchivo(file, nombreArchivo);
      const archivo: ArchivoSubido = {
        url: res.url,
        descripcion: '',
        fecha: new Date(),
        nombreOriginal: file.name,
        size: file.size, // <--- guardo tamaño en bytes para control total
      };
      this.archivos = [archivo];
      this.archivosChange.emit(this.archivos);
    } finally {
      this.loadingFile.emit(false);
      this.isUploading = false;
    }
  }

  async setFiles(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;

    if (this.maxFiles && this.maxFiles > 0) {
      const cantidadRestante = this.maxFiles - this.archivos.length;
      if (files.length > cantidadRestante) {
        this.helper.notifError(
          'Límite excedido',
          `Solo puedes subir ${this.maxFiles} archivos en total`,
        );
        return;
      }
    }

    // Validar tamaño total anticipado
    if (this.maxTotalSizeMB && this.maxTotalSizeMB > 0) {
      const maxTotalBytes = this.maxTotalSizeMB * 1024 * 1024;
      const archivosActualesBytes = this.totalSizeBytes;
      const archivosNuevosBytes = files.reduce((acc, f) => acc + f.size, 0);
      if (archivosActualesBytes + archivosNuevosBytes > maxTotalBytes) {
        this.helper.notifWarn(
          `La suma total de archivos supera el límite máximo de ${this.maxTotalSizeMB} MB.`,
        );
        return;
      }
    }

    const confirmed = await this.dialogService.confirm(
      'Subir archivos',
      `¿Querés subir ${files.length} archivo(s)?`,
    );
    if (!confirmed) return;

    this.loadingFile.emit(true);
    this.isUploading = true;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (this.maxSizeMB && this.maxSizeMB > 0) {
        const maxBytes = this.maxSizeMB * 1024 * 1024;
        if (file.size > maxBytes) {
          this.helper.notifWarn(
            `El archivo "${file.name}" supera el tamaño máximo permitido de ${this.maxSizeMB} MB.`,
          );
          continue;
        }
      }

      if (!this.esTipoValido(file)) {
        this.helper.notifWarn(`Tipo de archivo no permitido: ${file.type}`);
        continue;
      }

      const nombreArchivo = `${file.name.split('.')[0]}_${Date.now()}_${i}.${file.name.split('.').pop()}`;

      try {
        const res = await this.helper.subirArchivo(file, nombreArchivo);
        this.archivos.push({
          url: res.url,
          descripcion: '',
          fecha: new Date(),
          nombreOriginal: file.name,
          size: file.size, // guardo tamaño
        });
      } catch (error) {
        console.error(`Error al subir archivo ${file.name}`, error);
      }
    }

    this.archivosChange.emit(this.archivos);
    this.loadingFile.emit(false);
    this.isUploading = false;
    input.value = '';
  }

  eliminarArchivo(index: number): void {
    this.archivos.splice(index, 1);
    this.archivosChange.emit(this.archivos);
  }

  actualizarDescripcion(index: number, descripcion: string): void {
    this.archivos[index].descripcion = descripcion;
    this.archivosChange.emit(this.archivos);
  }
}
